import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EmployeeService } from '../../services/employee.service';

/**
 * Modal component for viewing and editing employee break policy and break-hour settings.
 * @component
 * @selector app-break-settings-modal
 * @example
 *   <app-break-settings-modal [isOpen]="true" (closeModal)="onClose()" (saveSettings)="onSaved($event)"></app-break-settings-modal>
 * @input {boolean} isOpen - Controls whether the modal is visible; when true the component attempts to load current break-hour settings.
 * @output {EventEmitter<void>} closeModal - Emitted when the modal is closed or cancelled (only emitted when not submitting).
 * @output {EventEmitter<any>} saveSettings - Emitted after successfully saving break-hour form settings (passes saved settings payload).
 *
 * Purpose:
 *   Handles display, validation and persistence of break-related settings (break duration, work-hours threshold,
 *   notification timing and email notification toggle). Manages enabling/disabling form controls based on a policy toggle
 *   and synchronizes the toggle state via a separate API call.
 *
 * Key methods:
 *   - initializeForm(): Builds the reactive FormGroup with default values and validators.
 *   - loadBreakHourSettings(): Loads settings from the API and populates the form; handles loading state and errors.
 *   - populateForm(data): Applies API data to the form and sets the enable/disable toggle appropriately.
 *   - onToggleBreakPolicy(): Toggles enableBreakPolicy, updates form state and immediately persists the toggle via PUT API.
 *   - onSave(): Validates and submits form field settings via POST API, emits saveSettings on success and closes the modal.
 *   - onClose()/onCancel(): Closes the modal (emits closeModal) if not currently submitting and clears validation state.
 *   - updateFormFieldStates(): Enables or disables form controls based on the policy toggle.
 *   - getFieldError(fieldName): Returns a human-friendly validation error message for a specific field.
 *   - incrementValue()/decrementValue(), isAtMinValue(), getMinValue(): Utility helpers to manage numeric controls.
 *   - resetSubmittingState(), clearValidationErrors(), resetFormState(): Helpers to reset form and submission state.
 *   - onNumberKeyDown(), onNumberInput(), onNumberPaste(): Prevent and sanitize non-numeric input for numeric fields.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Initializes the form and loads settings if the modal is initially open.
 *   - ngOnChanges(): Reloads settings whenever isOpen becomes true to ensure fresh data each time the modal opens.
 */
@Component({
  selector: 'app-break-settings-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './break-settings-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./break-settings-modal.component.scss'],
})
export class BreakSettingsModalComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() saveSettings = new EventEmitter<any>();

  breakForm!: FormGroup;
  isSubmitting = false;
  enableBreakPolicy = false;
  isLoading = false;
  currentSettings: any = null;

  constructor(
    private fb: FormBuilder,
    private employeeService: EmployeeService
  ) {}

  ngOnInit() {
    this.initializeForm();
    if (this.isOpen) {
      this.loadBreakHourSettings();
    }
  }

  ngOnChanges() {
    if (this.isOpen) {
      // Always load fresh settings when modal opens
      this.loadBreakHourSettings();
    }
  }

  initializeForm() {
    this.breakForm = this.fb.group({
      breakDuration: ['30', [Validators.required, Validators.min(1), Validators.max(480)]],
      workHoursThreshold: ['12', [Validators.required, Validators.min(1), Validators.max(24)]],
      notificationTime: ['30', [Validators.required, Validators.min(1), Validators.max(120)]],
      emailNotifications: [false],
    });

    // Initially disable all form fields until toggle is enabled
    this.updateFormFieldStates();
  }

  /**
   * Toggle the break policy enabled state, update related form field states, and immediately persist the change via the employeeService API; if the API call fails the toggle and form state are reverted.
   * @example
   * nToggleBreakPolicy()
   * undefined
   * @returns {{void}} No return value; performs state updates and side-effectful API call.
   **/
  onToggleBreakPolicy() {
    this.enableBreakPolicy = !this.enableBreakPolicy;
    this.updateFormFieldStates();

    // Call PUT API immediately when toggle is clicked
    this.employeeService.updateBreakPolicyStatus(this.enableBreakPolicy).subscribe({
      next: response => {
        if (response.status) {
          console.log('Break policy status updated successfully:', response.data);
        } else {
          console.error('Error updating break policy status:', response.message);
          // Revert the toggle state if API call failed
          this.enableBreakPolicy = !this.enableBreakPolicy;
          this.updateFormFieldStates();
        }
      },
      error: error => {
        console.error('Error updating break policy status:', error);
        // Revert the toggle state if API call failed
        this.enableBreakPolicy = !this.enableBreakPolicy;
        this.updateFormFieldStates();
      },
    });
  }

  toggleEmailNotifications() {
    const currentValue = this.breakForm.get('emailNotifications')?.value;
    this.breakForm.get('emailNotifications')?.setValue(!currentValue);
  }

  onClose() {
    if (!this.isSubmitting) {
      this.clearValidationErrors();
      this.closeModal.emit();
    }
  }

  onCancel() {
    this.onClose();
  }

  /**
   * Load break hour settings from the employee service, clearing validation errors, managing loading state, and populating the form when data is received.
   * @example
   * oadBreakHourSettings()
   * undefined
   * @returns {void} No return value; updates component state (isLoading, currentSettings) and populates the form or logs errors on failure.
   */
  loadBreakHourSettings() {
    this.isLoading = true;

    // Clear any existing validation errors before loading
    this.clearValidationErrors();

    this.employeeService.getBreakHourSettings().subscribe({
      next: response => {
        this.isLoading = false;
        if (response.status && response.data) {
          this.currentSettings = response.data;
          this.populateForm(response.data);
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error loading break hour settings:', error);
        // Keep default values if API fails
      },
    });
  }

  /**
   * Populate the break settings form and component state from an API response object.
   * @example
   * opulateForm({status: true, break_duration: 25, work_hours: 8, notify_before: 15, notify_via_email: true})
   * undefined
   * @param {{any}} {{data}} - API response object containing break settings (e.g. status, break_duration, work_hours, notify_before, notify_via_email).
   * @returns {{void}} No return value; updates component state and form controls.
   **/
  populateForm(data: any) {
    // Set the toggle state based on API response
    this.enableBreakPolicy = data.status;

    // Clear any existing validation errors before populating
    Object.keys(this.breakForm.controls).forEach(key => {
      this.breakForm.get(key)?.markAsUntouched();
      this.breakForm.get(key)?.markAsPristine();
    });

    // Populate form fields with current values from API
    this.breakForm.patchValue({
      breakDuration: data.break_duration || 30,
      workHoursThreshold: data.work_hours || 12,
      notificationTime: data.notify_before || 30,
      emailNotifications: data.notify_via_email || false,
    });

    // Enable/disable form fields based on toggle state
    this.updateFormFieldStates();
  }

  /**
   * Update the enabled/disabled states of break-related form controls based on the enableBreakPolicy flag.
   * @example
   * pdateFormFieldStates()
   * void
   * @returns {void} No return value; the method mutates form control states on this.breakForm.
   */
  updateFormFieldStates() {
    if (this.enableBreakPolicy) {
      this.breakForm.get('breakDuration')?.enable();
      this.breakForm.get('workHoursThreshold')?.enable();
      this.breakForm.get('notificationTime')?.enable();
      this.breakForm.get('emailNotifications')?.enable();
    } else {
      this.breakForm.get('breakDuration')?.disable();
      this.breakForm.get('workHoursThreshold')?.disable();
      this.breakForm.get('notificationTime')?.disable();
      this.breakForm.get('emailNotifications')?.disable();
    }
  }

  /**
   * Validate and submit break settings form: if valid and not already submitting, builds payload from form controls, calls employeeService.updateBreakHourFormSettings to persist settings, manages isSubmitting state, handles success (emits saved data and closes modal) and error cases; if invalid, marks all controls as touched to show validation errors.
   * @example
   * nSave()
   * // void
   * @param {void} - No arguments.
   * @returns {void} Does not return a value; performs side effects (API call, emits event, updates UI state).
   **/
  onSave() {
    if (this.breakForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      // Prepare form field data for POST API (excluding status - that's handled by PUT API)
      const formSettingsData = {
        break_duration: this.breakForm.get('breakDuration')?.value,
        work_hours: this.breakForm.get('workHoursThreshold')?.value,
        notify_before: this.breakForm.get('notificationTime')?.value,
        notify_via_email: this.breakForm.get('emailNotifications')?.value,
      };

      // Call POST API to update form field settings
      this.employeeService.updateBreakHourFormSettings(formSettingsData).subscribe({
        next: response => {
          this.isSubmitting = false;
          if (response.status) {
            console.log('Break hour form settings updated successfully:', response.data);
            this.saveSettings.emit(response.data);
            this.onClose();
          } else {
            console.error('Error saving break hour form settings:', response.message);
            // You might want to show an error message to the user here
          }
        },
        error: error => {
          this.isSubmitting = false;
          console.error('Error saving break hour form settings:', error);
          // You might want to show an error message to the user here
        },
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.breakForm.controls).forEach(key => {
        this.breakForm.get(key)?.markAsTouched();
      });
    }
  }

  // Called from parent component to reset form state
  resetSubmittingState() {
    this.isSubmitting = false;
  }

  // Clear validation errors without resetting form values
  clearValidationErrors() {
    if (this.breakForm) {
      Object.keys(this.breakForm.controls).forEach(key => {
        this.breakForm.get(key)?.markAsUntouched();
        this.breakForm.get(key)?.markAsPristine();
      });
    }
  }

  // Reset form state to clear validation errors and touched state
  /**
   * Reset and reinitialize the break settings reactive form to its default state, clear touched/pristine flags, and update related component state.
   * @example
   * esetFormState()
   * // No return value; the form is reset, default values are patched, and component state is updated.
   * @returns {void} No return value.
   **/
  resetFormState() {
    if (this.breakForm) {
      // Clear touched state for all form controls
      Object.keys(this.breakForm.controls).forEach(key => {
        this.breakForm.get(key)?.markAsUntouched();
        this.breakForm.get(key)?.markAsPristine();
      });

      // Reset form to its initial state
      this.breakForm.reset();

      // Re-initialize with default values
      this.breakForm.patchValue({
        breakDuration: '30',
        workHoursThreshold: '12',
        notificationTime: '30',
        emailNotifications: false,
      });

      // Reset toggle state
      this.enableBreakPolicy = false;
      this.updateFormFieldStates();
    }
  }

  getFieldError(fieldName: string): string {
    const field = this.breakForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldDisplayName(fieldName)} is required`;
      if (field.errors['min'])
        return `${this.getFieldDisplayName(fieldName)} must be at least ${field.errors['min'].min}`;
      if (field.errors['max'])
        return `${this.getFieldDisplayName(fieldName)} must be at most ${field.errors['max'].max}`;
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      breakDuration: 'Break Duration',
      workHoursThreshold: 'Work Hours Threshold',
      notificationTime: 'Notification Time',
    };
    return displayNames[fieldName] || fieldName;
  }

  incrementValue(fieldName: string) {
    const field = this.breakForm.get(fieldName);
    if (field) {
      const currentValue = parseInt(field.value, 10) || 0;
      field.setValue(currentValue + 1);
    }
  }

  decrementValue(fieldName: string) {
    const field = this.breakForm.get(fieldName);
    if (field) {
      const currentValue = parseInt(field.value) || 0;
      const minValue = this.getMinValue(fieldName);
      if (currentValue > minValue) {
        field.setValue(currentValue - 1);
      }
    }
  }

  // Check if a field is at its minimum value
  isAtMinValue(fieldName: string): boolean {
    const field = this.breakForm.get(fieldName);
    if (!field) return false;

    const currentValue = parseInt(field.value, 10) || 0;
    const minValue = this.getMinValue(fieldName);
    return currentValue <= minValue;
  }

  // Get minimum value for each field
  private getMinValue(fieldName: string): number {
    const minValues: Record<string, number> = {
      breakDuration: 1,
      workHoursThreshold: 1,
      notificationTime: 1,
    };
    return minValues[fieldName] || 1;
  }

  // Prevent invalid characters in number inputs
  /**
   * Handle keydown to allow only numeric input and common editing/navigation shortcuts
   * @example
   * nNumberKeyDown(sampleEvent)
   * undefined
   * @param {{KeyboardEvent}} {{event}} - Keydown event used to allow numeric keys, control shortcuts (Ctrl+A/C/V/X) and navigation/editing keys; prevents non-numeric input.
   * @returns {{void}} Does not return a value.
   **/
  onNumberKeyDown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      return;
    }

    // Ensure that it is a number and stop the keypress
    if (
      (event.shiftKey || event.keyCode < 48 || event.keyCode > 57) &&
      (event.keyCode < 96 || event.keyCode > 105)
    ) {
      event.preventDefault();
    }
  }

  // Clean up any invalid characters that might have been pasted
  /**
   * Handler that enforces numeric-only input and ensures a minimum value on the linked form control.
   * @example
   * nNumberInput({ target: { value: '12a', getAttribute: () => 'breakDuration' } })
   * undefined
   * @param {{any}} {{event}} - Event object from the input change containing the target input element.
   * @returns {{void}} Return description in one line.
   **/
  onNumberInput(event: any): void {
    const input = event.target;
    const value = input.value;

    // Remove any non-numeric characters
    const cleanValue = value.replace(/[^0-9]/g, '');

    // Update the input value if it was changed
    if (value !== cleanValue) {
      input.value = cleanValue;
      // Trigger the form control update
      const formControlName = input.getAttribute('formControlName');
      if (formControlName) {
        this.breakForm.get(formControlName)?.setValue(cleanValue);
      }
    }

    // Ensure minimum value is enforced
    const formControlName = input.getAttribute('formControlName');
    if (formControlName) {
      const field = this.breakForm.get(formControlName);
      if (field) {
        const currentValue = parseInt(cleanValue, 10) || 0;
        const minValue = this.getMinValue(formControlName);
        if (currentValue < minValue) {
          field.setValue(minValue);
          input.value = minValue.toString();
        }
      }
    }
  }

  // Handle paste events to clean up pasted content
  /**
   * Prevent default paste, extract only digits from the clipboard text, set the input value to those digits and update the corresponding Angular form control.
   * @example
   * nNumberPaste(event)
   * void
   * @param {{ClipboardEvent}} {{event}} - Clipboard paste event from an input element; used to obtain pasted text and target input.
   * @returns {{void}} No return value; updates the input element and form control as a side effect.
   **/
  onNumberPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      const cleanText = pastedText.replace(/[^0-9]/g, '');

      if (cleanText) {
        const input = event.target as HTMLInputElement;
        input.value = cleanText;

        // Update the form control
        const formControlName = input.getAttribute('formControlName');
        if (formControlName) {
          this.breakForm.get(formControlName)?.setValue(cleanText);
        }
      }
    }
  }
}
